import { useState, useEffect, useCallback } from 'react';
import { conversationsService } from '@/services/conversations';
import { getStaticConversations } from '@/config/static/conversations';
import { STORAGE_KEYS } from '@/utils/constants';
import type { Conversation, CreateConversationRequest, UserRole } from '@/types';

function getIdentity() {
  return {
    userId: localStorage.getItem(STORAGE_KEYS.USER_ID) || 'demo-user',
    userEmail: localStorage.getItem(STORAGE_KEYS.USER_EMAIL) || 'demo@local',
    userName: localStorage.getItem(STORAGE_KEYS.USER_NAME) || 'Demo user',
  };
}

export function useConversations(role: UserRole = 'novice') {
  const [conversations, setConversations] = useState<Conversation[]>(() => getStaticConversations(role));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchConversations = useCallback(async ({ showLoading = true }: { showLoading?: boolean } = {}) => {
    try {
      if (showLoading) setIsLoading(true);
      const data = await conversationsService.getAll();
      setConversations(data);
      setError(null);
    } catch (err) {
      // UI-first fallback (design parity / offline dev)
      setConversations(getStaticConversations(role));
      setError(null);
    } finally {
      if (showLoading) setIsLoading(false);
    }
  }, [role]);

  useEffect(() => {
    // Keep UI responsive by seeding with static data first
    setConversations(getStaticConversations(role));
    void fetchConversations({ showLoading: false });
  }, [fetchConversations, role]);

  const createConversation = useCallback(
    async (data: CreateConversationRequest) => {
      try {
        const newConversation = await conversationsService.create(data);
        setConversations((prev) => [newConversation, ...prev]);
        return newConversation;
      } catch (err) {
        const identity = getIdentity();
        const now = new Date().toISOString();
        const localConversation: Conversation = {
          id: `local-${Date.now().toString(36)}`,
          title: data.title?.trim() ? data.title : 'Conversation',
          messageCount: 0,
          lastMessage: '',
          createdAt: now,
          updatedAt: now,
          ...identity,
        };
        setConversations((prev) => [localConversation, ...prev]);
        setError(null);
        return localConversation;
      }
    },
    []
  );

  const deleteConversation = useCallback(async (id: string) => {
    try {
      await conversationsService.delete(id);
      setConversations((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      // Best-effort removal in offline mode
      setConversations((prev) => prev.filter((c) => c.id !== id));
      setError(null);
    }
  }, []);

  return {
    conversations,
    isLoading,
    error,
    fetchConversations,
    createConversation,
    deleteConversation,
  };
}
